export class Filter {
  filterId: string;
  filterName: string;
  filters: FilterItem[] = [];
}

export class FilterItem {
  id: string;
  ruleGroups: RuleGroup[];
}

export class RuleGroup {
  id: string;
  ruleType: RuleGroupTypes;
  ruleGroups: RuleGroup[];
  rules: Rule[];
}

export class Rule {
  id: string;
  datasetId: number;
  field: string; //Left side of the rule
  operator: RuleOperations; //Operator of the rule
  values: string[]; //Right side of the rule - values (static or field reference based on valueType)
  valueType: RuleValueType; //Type of value (static or field reference)
}

export enum RuleGroupTypes {
  And = 'AND',
  Or = 'OR'
}

export enum RuleOperations {
  Equals = 'EQUAL',
  NotEquals = 'NOT_EQUAL',
  GreaterThan = 'GREATER_THAN',
  LessThan = 'LESS_THAN',
  GreaterThanOrEqual = 'GREATER_THAN_OR_EQUAL',
  LessThanOrEqual = 'LESS_THAN_OR_EQUAL',
  In = 'IN',
  NotIn = 'NOT_IN',
  Like = 'LIKE',
  NotLike = 'NOT_LIKE',
  Between = 'BETWEEN',
  NotBetween = 'NOT_BETWEEN'
}

export enum FilterSelectionTypes {
  Multiple = 'Multiple Pick',
  Single = 'Single Pick',
}

export enum FilterTypes {
  Expanded = 'Expanded',
  Compacted = 'Compacted',
}

export enum RuleValueType {
  Static = 'Static',
  FieldReference = 'FieldReference'
}

export interface OperatorDefinition {
  operator: RuleOperations;
  name: string;
  minValues: number;
  maxValues: number;
  description: string;
  supportedDataTypes: string[]; // Array of supported data types
}

export class OperatorHelper {
  static getOperatorDefinitions(): OperatorDefinition[] {
    return [
      {
        operator: RuleOperations.Equals,
        name: 'Equals',
        minValues: 1,
        maxValues: 1,
        description: 'Field equals the specified value',
        supportedDataTypes: ['String', 'Integer', 'Decimal', 'DateTime', 'Boolean']
      },
      {
        operator: RuleOperations.NotEquals,
        name: 'Not Equals',
        minValues: 1,
        maxValues: 1,
        description: 'Field does not equal the specified value',
        supportedDataTypes: ['String', 'Integer', 'Decimal', 'DateTime', 'Boolean']
      },
      {
        operator: RuleOperations.GreaterThan,
        name: 'Greater Than',
        minValues: 1,
        maxValues: 1,
        description: 'Field is greater than the specified value',
        supportedDataTypes: ['Integer', 'Decimal', 'DateTime']
      },
      {
        operator: RuleOperations.LessThan,
        name: 'Less Than',
        minValues: 1,
        maxValues: 1,
        description: 'Field is less than the specified value',
        supportedDataTypes: ['Integer', 'Decimal', 'DateTime']
      },
      {
        operator: RuleOperations.GreaterThanOrEqual,
        name: 'Greater Than or Equal',
        minValues: 1,
        maxValues: 1,
        description: 'Field is greater than or equal to the specified value',
        supportedDataTypes: ['Integer', 'Decimal', 'DateTime']
      },
      {
        operator: RuleOperations.LessThanOrEqual,
        name: 'Less Than or Equal',
        minValues: 1,
        maxValues: 1,
        description: 'Field is less than or equal to the specified value',
        supportedDataTypes: ['Integer', 'Decimal', 'DateTime']
      },
      {
        operator: RuleOperations.In,
        name: 'In',
        minValues: 1,
        maxValues: -1, // -1 means unlimited
        description: 'Field value is in the specified list of values',
        supportedDataTypes: ['String', 'Integer', 'Decimal', 'DateTime', 'Boolean']
      },
      {
        operator: RuleOperations.NotIn,
        name: 'Not In',
        minValues: 1,
        maxValues: -1, // -1 means unlimited
        description: 'Field value is not in the specified list of values',
        supportedDataTypes: ['String', 'Integer', 'Decimal', 'DateTime', 'Boolean']
      },
      {
        operator: RuleOperations.Like,
        name: 'Like',
        minValues: 1,
        maxValues: 1,
        description: 'Field matches the specified pattern (use % for wildcards)',
        supportedDataTypes: ['String']
      },
      {
        operator: RuleOperations.NotLike,
        name: 'Not Like',
        minValues: 1,
        maxValues: 1,
        description: 'Field does not match the specified pattern (use % for wildcards)',
        supportedDataTypes: ['String']
      },
      {
        operator: RuleOperations.Between,
        name: 'Between',
        minValues: 2,
        maxValues: 2,
        description: 'Field value is between the two specified values (inclusive)',
        supportedDataTypes: ['Integer', 'Decimal', 'DateTime']
      },
      {
        operator: RuleOperations.NotBetween,
        name: 'Not Between',
        minValues: 2,
        maxValues: 2,
        description: 'Field value is not between the two specified values (inclusive)',
        supportedDataTypes: ['Integer', 'Decimal', 'DateTime']
      }
    ];
  }

  static getOperatorDefinition(operator: RuleOperations): OperatorDefinition | undefined {
    return this.getOperatorDefinitions().find(def => def.operator === operator);
  }

  static getOperatorName(operator: RuleOperations): string {
    const definition = this.getOperatorDefinition(operator);
    return definition?.name || operator;
  }

  static getMinValues(operator: RuleOperations): number {
    const definition = this.getOperatorDefinition(operator);
    return definition?.minValues || 1;
  }

  static getMaxValues(operator: RuleOperations): number {
    const definition = this.getOperatorDefinition(operator);
    return definition?.maxValues || 1;
  }

  static canAddMoreValues(operator: RuleOperations, currentValueCount: number): boolean {
    const maxValues = this.getMaxValues(operator);
    return maxValues === -1 || currentValueCount < maxValues;
  }

  static canRemoveValues(operator: RuleOperations, currentValueCount: number): boolean {
    const minValues = this.getMinValues(operator);
    return currentValueCount > minValues;
  }

  static isOperatorSupportedForDataType(operator: RuleOperations, dataType: string): boolean {
    const definition = this.getOperatorDefinition(operator);
    return definition ? definition.supportedDataTypes.includes(dataType) : false;
  }

  static getSupportedOperatorsForDataType(dataType: string): RuleOperations[] {
    return this.getOperatorDefinitions()
      .filter(def => def.supportedDataTypes.includes(dataType))
      .map(def => def.operator);
  }
}

export class FiltersDto {
  filters: Filter[];
}

// export class FilterDto {
//   filterId: string;
//   filterName: string;
//   filterType: FilterTypes;
//   selectionType: FilterSelectionTypes;
//   filters: FilterItem[];
// }

// export class FilterItem {
//   id: string;
//   datasetId: number;
//   ruleGroups: RuleGroup[];
// }
